import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { usageApi, UsageData } from "api/usage";
import { useAuthContext } from "auth/AuthProvider";
import { PERMISSIONS } from "auth/permissions";

interface BreadcrumbItem {
  name: string;
  typeName?: string;
  id: string;
}

interface LibraryContextType {
  currentFolderId: string;
  setCurrentFolderId: (id: string) => void;
  breadcrumb: BreadcrumbItem[];
  setBreadcrumb: (items: BreadcrumbItem[]) => void;
  query: string;
  setQuery: (q: string) => void;
  isDocumentLimitReached: boolean;
  isWorkflowLimitReached: boolean;
  isLoadingUsage: boolean;
  usageData: UsageData | null;
  refreshUsageData: () => Promise<void>;
}

export const LibraryContext = createContext<LibraryContextType | undefined>(
  undefined
);

const SESSION_KEY = "library_context";

function getInitialState() {
  if (typeof window !== "undefined") {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {}
    }
  }
  return {
    currentFolderId: "/",
    breadcrumb: [],
    query: "",
  };
}

export const LibraryProvider = ({ children }: { children: ReactNode }) => {
  const [currentFolderId, setCurrentFolderIdState] = useState<string>(
    getInitialState().currentFolderId
  );
  const [breadcrumb, setBreadcrumbState] = useState<BreadcrumbItem[]>(
    getInitialState().breadcrumb
  );

  const [query, setQueryState] = useState<string>(getInitialState().query);
  const [isLoadingUsage, setIsLoadingUsage] = useState(true);
  const [usageData, setUsageData] = useState<UsageData | null>(null);

  // Compute limit states from usageData using useMemo
  const isDocumentLimitReached = useMemo(() => {
    if (
      !usageData ||
      usageData.total_document_storage_size_mb === null ||
      usageData.max_storage_size_mb === null
    ) {
      return false;
    }
    return (
      usageData.total_document_storage_size_mb >= usageData.max_storage_size_mb
    );
  }, [usageData]);

  const isWorkflowLimitReached = useMemo(() => {
    if (
      !usageData ||
      usageData.total_workflows === null ||
      usageData.max_workflows === null
    ) {
      return false;
    }
    return usageData.total_workflows >= usageData.max_workflows;
  }, [usageData]);

  const { hasPermission } = useAuthContext();
  const canUploadDocument = hasPermission(PERMISSIONS.UPLOAD_DOCUMENT);

  const refreshUsageData = useCallback(async () => {
    if (!canUploadDocument) {
      setIsLoadingUsage(false);
      return;
    }

    try {
      setIsLoadingUsage(true);
      const currentDate = new Date(Date.now()).toISOString().split("T")[0];
      const data = await usageApi.getUsageOverview({
        start_date: currentDate,
        end_date: currentDate,
      });

      setUsageData(data);
    } catch (err) {
      console.error("Error fetching usage data:", err);
      // Don't block the upload if API fails
      setUsageData(null);
    } finally {
      setIsLoadingUsage(false);
    }
  }, [canUploadDocument]);

  // Fetch usage data on mount and when permissions change
  useEffect(() => {
    refreshUsageData();
  }, [refreshUsageData]);

  // Persist to session storage
  useEffect(() => {
    sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({ currentFolderId, breadcrumb, query })
    );
    return () => {
      sessionStorage.removeItem(SESSION_KEY);
    };
  }, [currentFolderId, breadcrumb, query]);

  // Update breadcrumb when folder changes (if needed)
  const setCurrentFolderId = (id: string) => {
    setCurrentFolderIdState(id);
    // Optionally update breadcrumb here if you want automatic breadcrumb logic
  };

  const setBreadcrumb = (items: BreadcrumbItem[]) => {
    setBreadcrumbState(items);
  };

  const setQuery = (q: string) => {
    setQueryState(q);
  };

  return (
    <LibraryContext.Provider
      value={{
        currentFolderId,
        setCurrentFolderId,
        breadcrumb,
        setBreadcrumb,
        query,
        setQuery,
        isDocumentLimitReached,
        isWorkflowLimitReached,
        isLoadingUsage,
        usageData,
        refreshUsageData,
      }}
    >
      {children}
    </LibraryContext.Provider>
  );
};

export function useLibraryContext() {
  const ctx = useContext(LibraryContext);
  if (!ctx)
    throw new Error("useLibraryContext must be used within LibraryProvider");
  return ctx;
}
